import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Config from '../../config';
import { Grid, Card, Toolbar, CardContent, Divider, Button, Dialog, DialogContent, Typography, TextField, DialogActions } from '@mui/material';
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import DownloadButton from '../../components/download_button';
import Moment from 'moment';
import Auth from '../../auth';

type SourcingItem = {
    SupplierId: number | string;
    Priority: number | null;
    Name: string;
};

// react-sortable-hoc's generic HOC signatures don't infer custom prop shapes
// cleanly through JSX; cast the wrapped render functions (and the resulting
// components) to any at the boundary, same pattern as
// catalogmanager/.../SortableImages.tsx.
const Draggable: any = SortableHandle((({children, style}: {children?: React.ReactNode; style?: React.CSSProperties}) => (
  <div style={{ cursor: "move", ...style }}>{children}</div>
)) as any);

const SortableItem: any = SortableElement((({ item: {SupplierId, Priority, Name} }: {item: SourcingItem}) => (
    <Draggable>
        <div style={{display: 'flex', flexDirection: 'row', padding: '0.25em', backgroundColor: 'white', minHeight: '45px'}}>
            <div style={{display: 'flex', alignItems: 'center', width: '5em', fontSize: '16px'}}>
                <div style={{width: '3em', textAlign: 'center'}}>{Priority === null ? 'Not Set' : Priority}</div>
            </div>
            <div style={{flex: '1 1 auto', textAlign: 'left', display: 'flex', alignItems: 'center'}}>{Name}</div>
        </div>
        <Divider/>
    </Draggable>
)) as any);

const SortableList: any = SortableContainer((({ items, disabled }: {items: SourcingItem[]; options?: any; disabled?: boolean}) => {
  return (
    <div>
      {items.map((item, index) => (
        <SortableItem disabled={disabled} key={`item-${item.SupplierId}`} item={item} index={index}/>
      ))}
    </div>
  );
}) as any);

type SourcingProps = {
    setTitle?: (title: string, flag?: boolean) => void;
    isRetailer?: boolean;
};

type SourcingState = {
    showError: boolean;
    showScorecards: boolean;
    pristine: boolean;
    start: string;
    end: string;
    prioritized?: SourcingItem[];
    current?: SourcingItem[];
    unprioritized?: SourcingItem[];
    error?: string;
};

const cmp = function(a: any, b: any): number {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
};

const sortPrioritized = (a: SourcingItem, b: SourcingItem) => cmp(a.Priority == null ? -1 : a.Priority, b.Priority == null ? -1 : b.Priority) || cmp(a.Name, b.Name);

const move = (items: SourcingItem[], oldIndex: number, newIndex: number) => {
    items = items.slice().map(r => Object.assign({}, r));
    items.splice(newIndex < 0 ? items.length + newIndex : newIndex, 0, items.splice(oldIndex, 1)[0]);
    var priority = 1;
    for(var i = 0; i < items.length; i++){
        if(items[i].Priority != null || priority > 1 || i === newIndex){
            items[i].Priority = priority++;
        }
    }
    return items;
};

const Sourcing = (props: SourcingProps) => {
    const [state, setState] = useState<SourcingState>(() => ({
        showError: false,
        showScorecards: false,
        pristine: true,
        start: Moment().add(-7, 'days').format('YYYY-MM-DD'),
        end: Moment().format('YYYY-MM-DD')
    }));

    const mergeState = (partial: Partial<SourcingState>) =>
        setState(prev => ({...prev, ...partial}));

    const checkPristine = (prioritized: SourcingItem[]) => prioritized.every(r => state.current!.find(x => x.SupplierId === r.SupplierId)!.Priority === r.Priority);

    const loadSourcing = async () => {
        let sourcings = (await axios.get(Config.api + '/odata/Company/Sourcing')).data.value;
        let partners = (await axios.get(Config.api + '/api/v1/Partners')).data.Body.Partners;
        let prioritized: SourcingItem[] = sourcings.filter((r: any) => partners.some((x: any) => x.Id === r.SupplierId)).map((r: any) => { return {SupplierId: r.SupplierId, Priority: r.Priority, Name: partners.find((x: any) => x.Id === r.SupplierId).CompanyName}});
        let unprioritized: SourcingItem[] = partners.filter((r: any) => !sourcings.find((x: any) => x.SupplierId === r.Id)).map((r: any) => { return {SupplierId: r.Id, Priority: null, Name: r.CompanyName}});
        prioritized = prioritized.concat(unprioritized).sort(sortPrioritized);
        mergeState({prioritized, current: prioritized, unprioritized, pristine: true});
    };

    useEffect(() => {
        if(props.setTitle) {
            props.setTitle('Sourcing Priority', true);
        }
        loadSourcing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sortEnd = ({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
        var prioritized = move(state.prioritized!, oldIndex, newIndex);
        var pristine = checkPristine(prioritized);
        mergeState({prioritized, pristine});
    };

    const save = async () => {
        var toSave = state.prioritized!.filter(r => r.Priority !== null).map(({SupplierId, Priority}) => ({SupplierId, Priority}));
        try{
            await axios.put(Config.api + '/odata/Company/Functions.UpdateSourcing', toSave);
        }catch(e){
            mergeState({showError: true, error: "Failed to save updated priorities."});
            return;
        }
        await loadSourcing();
    };

    const updateFromScorecards = async () => {
        var startDate = Moment(state.start).toISOString();
        var endDate = Moment(state.end).toISOString();
        var scorecards = (await axios.get(Config.api + `/odata/Company/Functions.GetSupplierScoreCards?$orderby=CombinedScore desc&startDate=${startDate}&endDate=${endDate}`)).data.value;
        var prioritized = state.prioritized!.slice().map(r => Object.assign({}, r));
        prioritized.forEach(item => {
            let scorecard = scorecards.find((r: any) => r.PartnerCoId === item.SupplierId);
            if(!scorecard){
                item.Priority = null;
            }else{
                item.Priority = scorecards.indexOf(scorecard) + 1;
            }
        });
        prioritized = prioritized.sort(sortPrioritized);
        var pristine = checkPristine(prioritized);
        mergeState({showScorecards: false, prioritized, pristine});
    };

    var canManageSettings = Auth.hasPermission('settings/manage');
    if(!props.isRetailer){
        return <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <CardContent style={{textAlign: 'left'}}>Only retailers can configure supplier sourcing priority.</CardContent>
                </Card>
            </Grid>
        </Grid>;
    }
    return (
        <Grid container spacing={2}>
            <Grid size={{ md: 12 }}>
                <Card>
                    <Toolbar className='lbtoolbar'>Supplier Sourcing Priority</Toolbar>
                    <CardContent style={{paddingRight: 0, paddingLeft: 0}}>
                    <div style={{marginBottom: '1em', textAlign: 'left', paddingLeft: '1em'}}>If you are using cost-based sourcing you can specify supplier priority below. This will be used to break ties if an order can be sourced to multiple suppliers at the same cost.</div>
                    {canManageSettings && <div style={{textAlign: 'left', paddingLeft: '1em'}}>
                        <Button variant='outlined' style={{marginRight: '1em'}} onClick={() => mergeState({showScorecards: true})}>Update from scorecards</Button>
                        <DownloadButton disabled={state.pristine} color='primary' variant='contained' onClick={save}>Save</DownloadButton>
                    </div>}
                    <div>
                        <div style={{height: '48px', display: 'flex', flexDirection: 'row', padding: '0.25em', backgroundColor: 'white', color: 'rgba(0, 0, 0, 0.54)', fontSize: '0.75rem'}}>
                            <div style={{justifyContent: 'center', alignItems: 'center', display: 'flex', width: '5em'}}>{'Priority'}</div>
                            <div style={{flex: '1 1 auto', textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '1.5em'}}>{'Supplier'}</div>
                        </div>
                        <Divider/>
                    </div>
                    {state.prioritized ?
                    <SortableList helperClass={'dragElement'} useDragHandle={true} disabled={!canManageSettings} items={state.prioritized} onSortEnd={sortEnd}/> : ''}
                    </CardContent>
                </Card>
            </Grid>
            <Dialog open={state.showError} onClose={() => {mergeState({showError: false})}}>
                <Toolbar className='lbtoolbar'>{'Error'}</Toolbar>
                <DialogContent><Typography style={{whiteSpace: "pre-wrap"}}>{state.error}</Typography></DialogContent>
            </Dialog>
            <Dialog maxWidth='sm' fullWidth={true} open={state.showScorecards} onClose={() => {mergeState({showScorecards: false})}}>
                <Toolbar className='lbtoolbar'>{'Update From Supplier Scorecards'}</Toolbar>
                <DialogContent>
                    <Typography style={{marginBottom: '1em'}}>
                    Please choose the date range for calculating scorecards and then click update to continue.
                    </Typography>
                    <TextField
                        style={{marginRight: '1em'}}
                        label="Start Date"
                        type="date"
                        value={state.start}
                        onChange={(e) => mergeState({start: e.target.value})}
                        slotProps={{
                            htmlInput: {max: state.end},
                            inputLabel: {shrink: true}
                        }} />
                    <TextField
                        label="End Date"
                        type="date"
                        value={state.end}
                        onChange={(e) => mergeState({end: e.target.value})}
                        slotProps={{
                            htmlInput: {min: state.start},
                            inputLabel: {shrink: true}
                        }} />
                </DialogContent>
                <DialogActions>
                    <DownloadButton color="primary" onClick={updateFromScorecards}>Update</DownloadButton>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default Sourcing;
